import * as React from "react"
import * as SwitchPrimitives from "@radix-ui/react-switch"

import { cn } from "@/lib/utils"

/*
  VeriHandi Switch — high-contrast, ON/OFF labelled, fully tokenised.

  All visual decisions live in CSS variables defined in /index.css:
    --switch-track-on, --switch-track-on-border
    --switch-track-off, --switch-track-off-border
    --switch-thumb, --switch-thumb-ring
    --switch-label-on, --switch-label-off

  Adjusting the toggle visuals app-wide = one variable edit. No JSX changes.
*/
const Switch = React.forwardRef(({ className, ...props }, ref) => (
  <SwitchPrimitives.Root
    className={cn(
      "group relative inline-flex h-7 w-14 shrink-0 cursor-pointer items-center rounded-full",
      "border-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
      "focus-visible:ring-offset-2 focus-visible:ring-offset-background",
      "disabled:cursor-not-allowed disabled:opacity-50 shadow-inner",
      "data-[state=checked]:bg-[color:var(--switch-track-on)]",
      "data-[state=checked]:border-[color:var(--switch-track-on-border)]",
      "data-[state=unchecked]:bg-[color:var(--switch-track-off)]",
      "data-[state=unchecked]:border-[color:var(--switch-track-off-border)]",
      className,
    )}
    {...props}
    ref={ref}
  >
    {/* ON label — visible only when checked, sits left of the thumb */}
    <span
      aria-hidden="true"
      className="pointer-events-none absolute left-1.5 text-[10px] font-bold tracking-wider opacity-0 transition-opacity group-data-[state=checked]:opacity-100"
      style={{ color: "var(--switch-label-on)" }}
    >
      ON
    </span>
    {/* OFF label — visible only when unchecked, sits right of the thumb */}
    <span
      aria-hidden="true"
      className="pointer-events-none absolute right-1.5 text-[10px] font-bold tracking-wider opacity-100 transition-opacity group-data-[state=checked]:opacity-0"
      style={{ color: "var(--switch-label-off)" }}
    >
      OFF
    </span>
    <SwitchPrimitives.Thumb
      className={cn(
        "pointer-events-none relative z-10 block h-5 w-5 rounded-full",
        "shadow-md",
        "transition-transform data-[state=checked]:translate-x-7 data-[state=unchecked]:translate-x-1",
      )}
      style={{
        background: "var(--switch-thumb)",
        boxShadow: "0 2px 6px rgba(0,0,0,0.15), 0 0 0 1px var(--switch-thumb-ring)",
      }}
    />
  </SwitchPrimitives.Root>
))
Switch.displayName = SwitchPrimitives.Root.displayName

export { Switch }
